import { useEffect, useRef } from 'react';
import { asset } from './asset';

const FOLLOW_QUERY = '(hover: hover) and (pointer: fine)';
const SMOOTHING = 18;
const FRAME_RATE = 24;
const SEEK_EPSILON = 1 / FRAME_RATE;

const FACE = { x: 0.46, y: 0.21, rx: 0.07, ry: 0.12 };
const BODY_ZONES = [
  { x: 0.46, y: 0.20, rx: 0.08, ry: 0.14 },
  { x: 0.47, y: 0.48, rx: 0.13, ry: 0.26 },
  { x: 0.475, y: 0.80, rx: 0.10, ry: 0.20 },
];

const LOOK_SRC = `${asset('img/reactor.mp4')}?v=3`;
const ENTRANCE_SRC = `${asset('img/reactor-entrance.mp4')}?v=1`;
const CLICK_SRC = `${asset('img/reactor-clickReact.mp4')}?v=1`;
const ENTRANCE_CUT_AT = 5.15;
const CLICK_CUT_AT = 7;
const TAP_LOOK_MS = 1400; // touch only: how long he keeps looking toward a tap
const FADE_ENTRANCE_MS = 800;
const FADE_CLICK_MS = 480;

const GAZE_KEYS = [
  [0.000, 0.16],
  [0.125, 0.24],
  [0.250, 0.37],
  [0.375, 0.47],
  [0.500, 0.54],
  [0.625, 0.64],
  [0.750, 0.78],
  [0.875, 0.90],
];

function fadeVeil(progress) {
  if (progress <= 0 || progress >= 1) return 0;
  const rise = progress < 0.45 ? progress / 0.45 : (1 - progress) / 0.55;
  const smooth = rise * rise * (3 - 2 * rise);
  return smooth * 0.2;
}

function canFollowPointer() {
  return window.matchMedia(FOLLOW_QUERY).matches;
}

function coverLayout(video) {
  const rect = video.getBoundingClientRect();
  const videoW = video.videoWidth || 1920;
  const videoH = video.videoHeight || 1080;
  const videoAspect = videoW / videoH;
  const elAspect = rect.width / Math.max(rect.height, 1);

  let contentW = rect.width;
  let contentH = rect.height;
  let offsetX = 0;
  let offsetY = 0;

  if (elAspect > videoAspect) {
    contentH = rect.width / videoAspect;
    offsetY = (rect.height - contentH) / 2;
  } else {
    contentW = rect.height * videoAspect;
    offsetX = (rect.width - contentW) / 2;
  }

  return { rect, contentW, contentH, offsetX, offsetY };
}

function clientToVideoUV(video, clientX, clientY) {
  const { rect, contentW, contentH, offsetX, offsetY } = coverLayout(video);
  return {
    x: (clientX - rect.left - offsetX) / contentW,
    y: (clientY - rect.top - offsetY) / contentH,
  };
}

function inEllipse(uv, zone) {
  const dx = (uv.x - zone.x) / zone.rx;
  const dy = (uv.y - zone.y) / zone.ry;
  return dx * dx + dy * dy <= 1;
}

function isOnFace(uv) {
  return inEllipse(uv, FACE);
}

function isOnBody(uv) {
  return BODY_ZONES.some((zone) => inEllipse(uv, zone));
}

function wrapTime(time, duration) {
  if (!duration) return 0;
  const wrapped = time % duration;
  return wrapped < 0 ? wrapped + duration : wrapped;
}

function lerpWrapped(current, target, duration, factor) {
  let delta = target - current;
  const half = duration / 2;
  if (delta > half) delta -= duration;
  if (delta < -half) delta += duration;
  return wrapTime(current + delta * factor, duration);
}

function timeFromAngleTurns(turns, duration) {
  let angle = turns % 1;
  if (angle < 0) angle += 1;

  for (let i = 0; i < GAZE_KEYS.length; i += 1) {
    const [startAngle, startTime] = GAZE_KEYS[i];
    const next = GAZE_KEYS[(i + 1) % GAZE_KEYS.length];
    const endAngle = i === GAZE_KEYS.length - 1 ? 1 : next[0];
    let endTime = next[1];
    if (endTime < startTime) endTime += 1;

    if (angle >= startAngle && angle <= endAngle) {
      const mix = endAngle === startAngle ? 0 : (angle - startAngle) / (endAngle - startAngle);
      return wrapTime((startTime + (endTime - startTime) * mix) * duration, duration);
    }
  }

  return GAZE_KEYS[0][1] * duration;
}

function prepareVideo(video, { pauseWhenPlaying = false } = {}) {
  video.muted = true;
  video.defaultMuted = true;
  video.volume = 0;
  video.playsInline = true;
  video.preload = 'auto';
  video.autoplay = false;
  video.controls = false;

  const stopVolume = () => {
    video.muted = true;
    video.volume = 0;
  };
  const stopPlayback = () => video.pause();

  video.addEventListener('volumechange', stopVolume);
  if (pauseWhenPlaying) {
    video.addEventListener('play', stopPlayback);
  }

  return () => {
    video.removeEventListener('volumechange', stopVolume);
    if (pauseWhenPlaying) {
      video.removeEventListener('play', stopPlayback);
    }
  };
}

function HeroReactorVideo() {
  const lookRef = useRef(null);
  const entranceRef = useRef(null);
  const clickRef = useRef(null);
  const hitRef = useRef(null);
  const fadeRef = useRef(null);

  useEffect(() => {
    const look = lookRef.current;
    const entrance = entranceRef.current;
    const click = clickRef.current;
    const hit = hitRef.current;
    const veil = fadeRef.current;
    if (!look || !entrance || !click || !hit || !veil) return undefined;

    const stage = veil.parentElement;
    const durationRef = { current: 0 };
    const targetRef = { current: 0 };
    const displayedRef = { current: 0 };
    const pendingRef = { current: null };
    const seekingRef = { current: false };
    const followRef = { current: canFollowPointer() };
    const lastFrameRef = { current: performance.now() };
    const modeRef = { current: 'look' };
    const tapRef = { holdUntil: 0 };
    const fadeFx = {
      active: false,
      startedAt: 0,
      duration: FADE_ENTRANCE_MS,
      onEnd: null,
      resumeMode: null,
    };
    let rafId = 0;
    let seekUnlockId = 0;

    const show = (video) => video.classList.add('is-visible');
    const hide = (video) => video.classList.remove('is-visible');

    const restTime = () => 0.04 * durationRef.current;

    const setMode = (mode) => {
      modeRef.current = mode;
      if (mode === 'entrance') {
        show(entrance);
        hide(look);
        hide(click);
      } else if (mode === 'click') {
        show(click);
        hide(entrance);
        show(look);
      } else {
        show(look);
        hide(entrance);
        hide(click);
      }
    };

    const timeFromPointer = (clientX, clientY) => {
      const duration = durationRef.current;
      if (!duration) return 0;

      const uv = clientToVideoUV(look, clientX, clientY);
      if (isOnFace(uv)) {
        return restTime();
      }

      const layout = coverLayout(look);
      const headX = layout.rect.left + layout.offsetX + FACE.x * layout.contentW;
      const headY = layout.rect.top + layout.offsetY + FACE.y * layout.contentH;
      const angle = Math.atan2(clientY - headY, clientX - headX);
      let turns = (angle + Math.PI / 2) / (Math.PI * 2);
      if (turns < 0) turns += 1;
      if (turns >= 1) turns -= 1;
      return timeFromAngleTurns(turns, duration);
    };

    const snapToFrame = (time) => {
      const duration = durationRef.current;
      if (!duration) return 0;
      const frame = Math.round(time * FRAME_RATE);
      const maxFrame = Math.max(0, Math.round(duration * FRAME_RATE) - 1);
      return Math.min(maxFrame, Math.max(0, frame)) / FRAME_RATE;
    };

    const applySeek = () => {
      const next = pendingRef.current;
      if (next == null || seekingRef.current || !Number.isFinite(next)) return;
      if (look.readyState < 1) return;
      const snapped = snapToFrame(next);
      if (Math.abs(look.currentTime - snapped) < SEEK_EPSILON * 0.5) {
        pendingRef.current = null;
        return;
      }
      pendingRef.current = null;
      seekingRef.current = true;
      look.currentTime = snapped;
      window.clearTimeout(seekUnlockId);
      seekUnlockId = window.setTimeout(() => {
        seekingRef.current = false;
        applySeek();
      }, 90);
    };

    const queueSeek = (time) => {
      pendingRef.current = time;
      applySeek();
    };

    const unlockLook = () => {
      look.muted = true;
      look.volume = 0;
      const playPromise = look.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.then(() => look.pause()).catch(() => {});
      }
    };

    const startFade = (duration, onStart) => {
      if (fadeFx.active) return;
      modeRef.current = 'fade';
      fadeFx.active = true;
      fadeFx.startedAt = performance.now();
      fadeFx.duration = duration;
      fadeFx.onEnd = null;
      fadeFx.resumeMode = null;
      stage?.classList.toggle('is-fading-fast', duration <= FADE_CLICK_MS);
      stage?.classList.add('is-fading');
      veil.style.opacity = '0';
      onStart?.();
    };

    const endFade = () => {
      const next = fadeFx.resumeMode;
      const onEnd = fadeFx.onEnd;
      fadeFx.active = false;
      fadeFx.onEnd = null;
      fadeFx.resumeMode = null;
      veil.style.opacity = '0';
      stage?.classList.remove('is-fading', 'is-fading-fast');
      onEnd?.();
      if (next) {
        modeRef.current = next;
      }
    };

    const goToLook = ({ fade = false, duration = FADE_ENTRANCE_MS } = {}) => {
      if (modeRef.current === 'fade') return;
      if (modeRef.current === 'look' && look.classList.contains('is-visible') && !fade) {
        return;
      }

      const rest = restTime();
      targetRef.current = rest;
      displayedRef.current = rest;
      queueSeek(rest);

      if (!fade) {
        setMode('look');
        click.pause();
        entrance.pause();
        return;
      }

      startFade(duration, () => {
        show(look);
        hide(entrance);
        hide(click);
        fadeFx.resumeMode = 'look';
        fadeFx.onEnd = () => {
          click.pause();
          entrance.pause();
        };
      });
    };

    const playClip = (video) => {
      video.muted = true;
      video.volume = 0;
      try {
        if (video.readyState > 0) {
          video.currentTime = 0;
        }
      } catch {
        // Metadata may not be ready yet.
      }
      const attempt = () => {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            if (video.readyState < 2) {
              video.addEventListener('canplay', attempt, { once: true });
              return;
            }
            goToLook();
          });
        }
      };
      if (video.readyState >= 2) {
        attempt();
      } else {
        video.addEventListener('canplay', attempt, { once: true });
      }
    };

    const playClick = () => {
      if (modeRef.current === 'entrance' || modeRef.current === 'fade') return;
      try {
        if (click.readyState > 0) {
          click.currentTime = 0;
        }
      } catch {
        // Metadata may not be ready yet.
      }
      startFade(FADE_CLICK_MS, () => {
        show(click);
        hide(entrance);
        show(look);
        fadeFx.resumeMode = 'click';
        playClip(click);
      });
    };

    const syncDuration = () => {
      const duration = look.duration;
      if (!Number.isFinite(duration) || duration <= 0) return;
      durationRef.current = duration;
      const rest = restTime();
      targetRef.current = rest;
      displayedRef.current = rest;
      queueSeek(rest);
    };

    const tick = (now) => {
      rafId = window.requestAnimationFrame(tick);

      if (fadeFx.active) {
        const elapsed = now - fadeFx.startedAt;
        const progress = Math.min(1, elapsed / fadeFx.duration);
        veil.style.opacity = String(fadeVeil(progress));
        if (progress >= 1) {
          endFade();
        }
      }

      if (modeRef.current === 'entrance') {
        if (entrance.currentTime >= ENTRANCE_CUT_AT) {
          goToLook({ fade: true, duration: FADE_ENTRANCE_MS });
        }
        return;
      }
      if (modeRef.current === 'click') {
        if (click.currentTime >= CLICK_CUT_AT) {
          click.pause();
          goToLook({ fade: true, duration: FADE_CLICK_MS });
        }
        return;
      }
      if (modeRef.current !== 'look') return;
      const duration = durationRef.current;
      if (!duration) return;

      const dt = Math.min(0.05, Math.max(0, (now - lastFrameRef.current) / 1000));
      lastFrameRef.current = now;
      const factor = 1 - Math.exp(-SMOOTHING * dt);
      const target = (followRef.current || now < tapRef.holdUntil) ? targetRef.current : restTime();
      displayedRef.current = lerpWrapped(displayedRef.current, target, duration, factor);
      queueSeek(displayedRef.current);
    };

    const onPointerMove = (event) => {
      const uv = clientToVideoUV(look, event.clientX, event.clientY);
      hit.classList.toggle('is-over-body', isOnBody(uv));

      if (modeRef.current !== 'look') return;
      if (!followRef.current || event.pointerType === 'touch') return;
      if (!durationRef.current) return;
      targetRef.current = timeFromPointer(event.clientX, event.clientY);
    };

    const onPointerLeave = () => {
      hit.classList.remove('is-over-body');
      if (modeRef.current !== 'look') return;
      targetRef.current = restTime();
    };

    const onFollowChange = (event) => {
      followRef.current = event.matches;
      if (!event.matches && modeRef.current === 'look') {
        targetRef.current = restTime();
      }
    };

    const onSeeked = () => {
      window.clearTimeout(seekUnlockId);
      seekingRef.current = false;
      look.classList.add('is-ready');
      applySeek();
    };

    const onHitClick = (event) => {
      const uv = clientToVideoUV(look, event.clientX, event.clientY);
      if (!isOnBody(uv)) return;
      event.preventDefault();
      playClick();
    };

    // Touch devices have no hover, so the head stays still (rest pose) until the
    // visitor TAPS the background: he then glances toward the tap for a moment and
    // settles back. Taps on him keep playing the click reaction (onHitClick).
    // A no-op whenever a fine pointer is available, so desktop is unchanged.
    const onTapLook = (event) => {
      if (followRef.current) return;
      if (modeRef.current !== 'look' || !durationRef.current) return;
      const uv = clientToVideoUV(look, event.clientX, event.clientY);
      if (isOnBody(uv)) return;
      targetRef.current = timeFromPointer(event.clientX, event.clientY);
      tapRef.holdUntil = performance.now() + TAP_LOOK_MS;
    };

    const cleanupLook = prepareVideo(look, { pauseWhenPlaying: true });
    const cleanupEntrance = prepareVideo(entrance);
    const cleanupClick = prepareVideo(click);

    const onClipEnded = () => goToLook({ fade: true, duration: FADE_CLICK_MS });

    look.addEventListener('loadedmetadata', syncDuration);
    look.addEventListener('loadeddata', unlockLook, { once: true });
    look.addEventListener('durationchange', syncDuration);
    look.addEventListener('seeked', onSeeked);
    look.addEventListener('canplay', applySeek);
    entrance.addEventListener('ended', onClipEnded);
    click.addEventListener('ended', onClipEnded);
    hit.addEventListener('click', onHitClick);
    hit.addEventListener('click', onTapLook);
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    document.documentElement.addEventListener('mouseleave', onPointerLeave);

    const media = window.matchMedia(FOLLOW_QUERY);
    if (media.addEventListener) {
      media.addEventListener('change', onFollowChange);
    } else {
      media.addListener(onFollowChange);
    }

    if (look.readyState >= 1) {
      syncDuration();
    }

    setMode('entrance');
    playClip(entrance);

    rafId = window.requestAnimationFrame(tick);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(seekUnlockId);
      endFade();
      cleanupLook();
      cleanupEntrance();
      cleanupClick();
      look.removeEventListener('loadedmetadata', syncDuration);
      look.removeEventListener('loadeddata', unlockLook);
      look.removeEventListener('durationchange', syncDuration);
      look.removeEventListener('seeked', onSeeked);
      look.removeEventListener('canplay', applySeek);
      entrance.removeEventListener('ended', onClipEnded);
      click.removeEventListener('ended', onClipEnded);
      hit.removeEventListener('click', onHitClick);
      hit.removeEventListener('click', onTapLook);
      window.removeEventListener('pointermove', onPointerMove);
      document.documentElement.removeEventListener('mouseleave', onPointerLeave);
      if (media.removeEventListener) {
        media.removeEventListener('change', onFollowChange);
      } else {
        media.removeListener(onFollowChange);
      }
    };
  }, []);

  return (
    <div className="hero-reactor-stage">
      <video
        ref={lookRef}
        className="hero-reactor hero-reactor-look"
        src={LOOK_SRC}
        muted
        playsInline
        preload="auto"
        autoPlay={false}
        controls={false}
        disablePictureInPicture
        disableRemotePlayback
        aria-hidden="true"
      />
      <video
        ref={entranceRef}
        className="hero-reactor hero-reactor-clip"
        src={ENTRANCE_SRC}
        muted
        playsInline
        preload="auto"
        autoPlay
        controls={false}
        disablePictureInPicture
        disableRemotePlayback
        aria-hidden="true"
      />
      <video
        ref={clickRef}
        className="hero-reactor hero-reactor-clip"
        src={CLICK_SRC}
        muted
        playsInline
        preload="auto"
        autoPlay={false}
        controls={false}
        disablePictureInPicture
        disableRemotePlayback
        aria-hidden="true"
      />
      <button
        ref={hitRef}
        type="button"
        className="hero-reactor-hit"
        aria-label="Play character reaction"
      />
      <div
        ref={fadeRef}
        className="hero-reactor-fade"
        aria-hidden="true"
      />
    </div>
  );
}

export default HeroReactorVideo;
